import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useDeedPlotterContext } from '@/features/boundaries/deedPlotter/provider';
import { useCustomizeLandContext } from '@/features/customizeMyLand/provider';
import { RootState } from '@/redux/store';

import {
  CombinedFeatureTypes,
  FeatureGroupsEnum,
  featureGroupTypes,
  featureTypeFeatureSliceMap,
  FeatureTypeNonFeatureSliceEnum,
  FeatureTypeSelectionsPanelEnum,
} from '../../selectionsPanel/constants';
import { SelectionContent } from './selectionContent';
import { SelectionsAccordion } from './selectionsAccordion';

export const SelectionsContainer = () => {
  const features = useSelector((state: RootState) => state.features);

  const featureGroups = useMemo(() => {
    return Object.keys(featureGroupTypes);
  }, []);

  const featuresInFeatureSlice = useMemo(() => {
    return Object.keys(FeatureTypeSelectionsPanelEnum);
  }, []);

  const {
    customizations: { allCustomizations: customizations },
  } = useCustomizeLandContext();

  const { validGroups } = useDeedPlotterContext();

  const determineSelections = useCallback(
    (selectionType: CombinedFeatureTypes) => {
      if (featuresInFeatureSlice.includes(selectionType)) {
        return features[featureTypeFeatureSliceMap[selectionType]];
      }

      if (selectionType === FeatureTypeNonFeatureSliceEnum.CUSTOMIZATIONS) {
        return customizations;
      }

      if (selectionType === FeatureTypeNonFeatureSliceEnum.DEED_PLOTTER) {
        return validGroups;
      }

      return [];
    },
    [customizations.length, features, featuresInFeatureSlice, validGroups]
  );

  const featureGroupsMaped = useMemo(() => {
    return featureGroups.map((groupKey: FeatureGroupsEnum) => {
      const selectionTypes = featureGroupTypes[groupKey];
      if (selectionTypes.length <= 0) return null;
      if (selectionTypes.length > 1) {
        const selectionsForGroup = selectionTypes
          .map((selectionType) => determineSelections(selectionType))
          .flat();

        if (selectionsForGroup.length <= 0) return null;
        return (
          <SelectionsAccordion
            key={groupKey}
            darkAccordion
            content={
              <>
                {selectionTypes.map((selectionType) => {
                  const selections = determineSelections(selectionType);

                  if (selections.length <= 0) return null;

                  return (
                    <SelectionsAccordion
                      key={selectionType}
                      content={
                        <SelectionContent
                          selectionType={selectionType}
                          selections={selections}
                        />
                      }
                      darkAccordion={false}
                      selectionType={selectionType}
                      selections={selections}
                    />
                  );
                })}
              </>
            }
            contentClassName="p-0"
            selectionType={groupKey}
            selections={selectionsForGroup}
          />
        );
      }

      const selectionType = selectionTypes[0];

      const selections = determineSelections(selectionType);

      if (selections.length <= 0) return null;

      return (
        <SelectionsAccordion
          key={selectionType}
          darkAccordion
          content={
            <SelectionContent
              selectionType={selectionType}
              selections={selections}
            />
          }
          selectionType={selectionType}
          selections={selections}
        />
      );
    });
  }, [determineSelections, featureGroups]);

  return (
    <div className="relative flex flex-col overflow-y-auto scrollbar-hide">
      {featureGroupsMaped}
    </div>
  );
};
